import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, Maximize2, Loader2, AlertCircle, Clock, TrendingUp } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function ProjectsWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const result = await apiRequest('/projects/dashboard/my-projects');
      setData(result);
    } catch (error) {
      console.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A': return 'bg-green-100 text-green-800';
      case 'B': return 'bg-blue-100 text-blue-800';
      case 'C': return 'bg-yellow-100 text-yellow-800';
      case 'D': return 'bg-orange-100 text-orange-800';
      case 'F': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  return (
    <Card data-testid="projects-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FolderKanban className="h-5 w-5 text-indigo-500" />
            <CardTitle className="text-lg">Projects</CardTitle>
            {data?.total_projects > 0 && (
              <Badge variant="secondary">{data.total_projects}</Badge>
            )}
          </div>
          {expandable && (
            <Button variant="ghost" size="icon" onClick={() => navigate(expandPath || '/projects')} className="h-8 w-8">
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : !data || data.total_projects === 0 ? (
          <div className="text-center py-4 text-slate-400">
            <FolderKanban className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No active projects</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Urgent alert for tasks needing updates */}
            {data.tasks_needing_update_count > 0 && (
              <div className="flex items-center gap-2 p-2 bg-amber-50 rounded text-amber-700 text-xs border border-amber-200">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{data.tasks_needing_update_count} task(s) need updates</span>
              </div>
            )}

            {/* Project list */}
            {data.projects.slice(0, 3).map(project => (
              <div 
                key={project.id} 
                className="p-2 bg-slate-50 rounded border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors"
                onClick={() => navigate(`/admin/projects/${project.id}`)}
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="font-medium text-sm text-slate-900 line-clamp-1">{project.name}</p>
                  <Badge className={`text-xs ${getGradeColor(project.grade)}`}>
                    {project.score}%
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    {project.completed_tasks}/{project.task_count} tasks
                  </span>
                  {project.my_task_count > 0 && (
                    <span className="text-indigo-600 font-medium">
                      {project.my_task_count} assigned to you
                    </span>
                  )}
                </div>
              </div>
            ))}

            {/* View all link */}
            {data.total_projects > 3 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full text-xs text-slate-500"
                onClick={() => navigate('/admin/projects')}
              >
                View all {data.total_projects} projects
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
